import { useState } from "react";
import { ChevronsUpDown, Plus, Check, Building2 } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/quotationApi";

export default function WorkspaceSwitcher() {
  const { user, switchWorkspace, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);

  if (!user) return null;
  const workspaces = user.workspaces || [];
  const active = workspaces.find((w) => w.id === user.active_workspace_id);

  const onSwitch = async (id) => {
    if (id === user.active_workspace_id) return;
    try {
      await switchWorkspace(id);
      toast.success("Workspace switched");
    } catch {
      toast.error("Could not switch workspace");
    }
  };

  const onCreate = async () => {
    if (!name.trim()) {
      toast.error("Enter a workspace name");
      return;
    }
    setCreating(true);
    try {
      const r = await api.post("/workspaces", { name: name.trim() });
      await switchWorkspace(r.data.id);
      await refresh();
      toast.success("Workspace created");
      setOpen(false);
      setName("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create workspace");
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition max-w-[160px]"
            data-testid="workspace-switcher-trigger"
          >
            <Building2 size={14} className="text-[#C5A880] shrink-0" />
            <span className="text-[13px] font-medium truncate">
              {active?.name || "Workspace"}
            </span>
            <ChevronsUpDown size={13} className="text-white/40 shrink-0" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="bg-[#0A0C10] border-white/10 text-white w-56"
          data-testid="workspace-switcher-menu"
        >
          <DropdownMenuLabel className="text-white/40 text-[10px] uppercase tracking-widest">
            Your workspaces
          </DropdownMenuLabel>
          {workspaces.map((w) => (
            <DropdownMenuItem
              key={w.id}
              onClick={() => onSwitch(w.id)}
              className="cursor-pointer focus:bg-white/10 flex items-center justify-between"
              data-testid={`workspace-option-${w.id}`}
            >
              <span className="truncate">{w.name}</span>
              {w.id === user.active_workspace_id && (
                <Check size={14} className="text-[#C5A880]" />
              )}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator className="bg-white/10" />
          <DropdownMenuItem
            onClick={() => setOpen(true)}
            className="cursor-pointer focus:bg-white/10 text-[#C5A880]"
            data-testid="workspace-create-btn"
          >
            <Plus size={14} className="mr-2" /> New workspace
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A0C10] border-white/10 text-white" data-testid="create-workspace-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Create a new workspace</DialogTitle>
            <DialogDescription className="text-white/50">
              Each workspace is a separate business with its own price lists, branding and quotations.
            </DialogDescription>
          </DialogHeader>
          <label className="label-luxe">Business / workspace name</label>
          <input
            className="input-luxe"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Skyline Modular Interiors"
            onKeyDown={(e) => e.key === "Enter" && onCreate()}
            data-testid="create-workspace-name-input"
            autoFocus
          />
          <button
            onClick={onCreate}
            disabled={creating || !name.trim()}
            className="btn-primary w-full mt-2"
            data-testid="create-workspace-submit"
          >
            {creating ? "Creating…" : "Create workspace"}
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
}
